import React, { useState } from "react";
import axiosInstance from "../utils/AxiosInstance";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { useGoogleLogin } from "@react-oauth/google";
import ReCAPTCHA from "react-google-recaptcha";

const Register = () => {
  const { login, loginWithGoogle } = useAuth();
  const navigate = useNavigate();

  const [username, setUsername] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [recaptchaToken, setRecaptchaToken] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const handleInputChange = (setter) => (e) => {
    setter(e.target.value);
    setError("");
  };

  const handleRecaptchaChange = (token) => {
    setRecaptchaToken(token);
    setError("");
  };

  const handleRegister = async (e) => {
    e.preventDefault();

    if (password !== confirmPassword) {
      setError("Passwords do not match.");
      return;
    }

    if (!/[A-Z]/.test(password) || !/[0-9]/.test(password) || password.length < 8) {
      setError("Password must be at least 8 characters, contain a number, and an uppercase letter.");
      return;
    }

    if (!recaptchaToken) {
      setError("Please complete the reCAPTCHA.");
      return;
    }

    const userData = { username, email, password, recaptcha: recaptchaToken };

    try {
      setLoading(true);

      await axiosInstance.post("accounts/register/", userData);

      await login(email, password);

      navigate("/settings");
    } catch (err) {
      const errorMsg =
        err.response?.data?.detail ||
        err.response?.data?.email?.[0] ||
        err.response?.data?.username?.[0] ||
        "An error occurred during registration.";
      setError(errorMsg);
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const googleRegister = useGoogleLogin({
    onSuccess: async (tokenResponse) => {
      setError("");
      setLoading(true);
      try {
        await loginWithGoogle(tokenResponse.access_token);
        navigate("/settings");
      } catch (err) {
        setError("Google registration failed. Please try again.");
        console.error(err);
      } finally {
        setLoading(false);
      }
    },
    onError: () => setError("Google registration failed. Please try again."),
    flow: "implicit",
  });

  return (
    <div className="flex flex-col justify-center items-center h-screen">
      <h1 className="text-4xl font-bold text-purple-500">Register</h1>
      <form onSubmit={handleRegister} className="space-y-4 mt-8 w-72">
        <input
          type="text"
          placeholder="Username"
          value={username}
          onChange={handleInputChange(setUsername)}
          className="w-full p-2 bg-gray-800 text-white border border-gray-700 rounded"
          required
          disabled={loading}
        />
        <input
          type="email"
          placeholder="Email"
          value={email}
          onChange={handleInputChange(setEmail)}
          className="w-full p-2 bg-gray-800 text-white border border-gray-700 rounded"
          required
          disabled={loading}
        />
        <input
          type="password"
          placeholder="Password"
          value={password}
          onChange={handleInputChange(setPassword)}
          className="w-full p-2 bg-gray-800 text-white border border-gray-700 rounded"
          required
          disabled={loading}
        />
        <input
          type="password"
          placeholder="Confirm Password"
          value={confirmPassword}
          onChange={handleInputChange(setConfirmPassword)}
          className="w-full p-2 bg-gray-800 text-white border border-gray-700 rounded"
          required
          disabled={loading}
        />
        <div className="pt-2">
          <ReCAPTCHA
            sitekey="6LcnG2MrAAAAAK-dOaAsiatKzQWQWmNbVY4x-Os-"
            onChange={handleRecaptchaChange}
            className="mx-auto"
          />
        </div>
        {error && <p className="text-red-500 text-sm">{error}</p>}
        <button
          type="submit"
          className="w-full py-2 px-4 bg-purple-600 text-white rounded hover:bg-purple-700 disabled:opacity-50"
          disabled={loading}
        >
          {loading ? "Registering..." : "Register"}
        </button>
      </form>

      <div className="mt-6 w-72 text-center">
        <p className="mb-2 text-gray-300">Or register with Google</p>
        <button
          onClick={() => googleRegister()}
          className="w-full py-2 px-4 bg-red-600 text-white rounded hover:bg-red-700 disabled:opacity-50"
          disabled={loading}
        >
          Register with Google
        </button>
      </div>
    </div>
  );
};

export default Register;
